import { useCallback, useEffect, useRef, useState } from 'react'
import { discardFailedOutbox, failedOutboxSize, loadFailedOutbox, outboxSize } from '../offline/outbox'
import { DeadLetterPanel } from './DeadLetterPanel'

// Smarter Verbindungs-Punkt im Kopf (links neben dem Avatar): grün pulsierend = online & alles gespeichert,
// gelb = online, aber die Offline-Outbox hält noch nicht zugestellte Schreibvorgänge, rot = offline,
// alarm-rot „failed" = ein Schreibvorgang ist endgültig fehlgeschlagen (Dead-Letter) und braucht Aufmerksamkeit.
// Der Punkt bleibt wortlos — bei Hover/Fokus/Antippen fährt seitlich eine PILLE IN DER STATUSFARBE aus
// (Simons Vorgabe 13.07.: farbig ausfahren, kein grauer Infokasten). Im „failed"-Zustand öffnet Antippen
// zusätzlich das Dead-Letter-Panel (Liste der verlorenen Stempel + Verwerfen).
// (Nachfolger der ConnectionIndicator-Anzeige; Zustands-Logik der drei Grundzustände unverändert.)

type ConnState = 'online' | 'pending' | 'offline' | 'failed'

function readState(): ConnState {
  // 'failed' hat Vorrang: ein endgültig verlorener Schreibvorgang ist das wichtigste und einzige aktionable Signal.
  if (failedOutboxSize() > 0) return 'failed'
  if (!navigator.onLine) return 'offline'
  return outboxSize() > 0 ? 'pending' : 'online'
}

/** Voller Satz für Screenreader/Titel — Länge egal, wird nie visuell gelayoutet. `count` = failed- bzw. pending-Anzahl. */
function label(state: ConnState, count: number): string {
  if (state === 'failed')
    return count === 1
      ? '1 Stempel konnte nicht gespeichert werden — bitte prüfen'
      : `${count} Stempel konnten nicht gespeichert werden — bitte prüfen`
  if (state === 'offline') return 'Offline — Änderungen werden lokal gespeichert und später übertragen'
  if (state === 'pending')
    return count === 1
      ? 'Sync ausstehend — 1 Änderung wartet auf Übertragung'
      : `Sync ausstehend — ${count} Änderungen warten auf Übertragung`
  return 'Verbunden — Änderungen werden sofort gespeichert'
}

/** Kurzer Pillen-Text — muss einzeilig neben Punkt+Avatar auch auf 390-px-Screens passen. */
function pillText(state: ConnState, count: number): string {
  if (state === 'failed') return count === 1 ? '1 Stempel nicht gespeichert' : `${count} Stempel nicht gespeichert`
  if (state === 'offline') return 'Offline – wird lokal gespeichert'
  if (state === 'pending') return count === 1 ? '1 Änderung wartet auf Sync' : `${count} Änderungen warten auf Sync`
  return 'Verbunden – alles gespeichert'
}

export function SmartStatusIndicator() {
  const [state, setState] = useState<ConnState>(readState)
  const [pending, setPending] = useState(() => outboxSize())
  const [failed, setFailed] = useState(() => failedOutboxSize())
  const [open, setOpen] = useState(false) // Touch-Pfad: Tipp auf den Punkt pinnt Pille/Panel
  const ref = useRef<HTMLSpanElement>(null)

  const refresh = useCallback((): void => {
    setState(readState())
    setPending(outboxSize())
    setFailed(failedOutboxSize())
  }, [])

  useEffect(() => {
    // online/offline decken den Netz-Wechsel ab; focus + Intervall fangen den Outbox-Stand ein
    // (die Queue lebt in localStorage — dafür gibt es kein Event im selben Tab).
    window.addEventListener('online', refresh)
    window.addEventListener('offline', refresh)
    window.addEventListener('focus', refresh)
    const handle = setInterval(refresh, 5000)
    return () => {
      window.removeEventListener('online', refresh)
      window.removeEventListener('offline', refresh)
      window.removeEventListener('focus', refresh)
      clearInterval(handle)
    }
  }, [refresh])

  // Getipptes (gepinntes) Panel: Tipp/Klick außerhalb oder Escape schließt — wie ProfileMenu/NavMenu.
  useEffect(() => {
    if (!open) return
    function onPointerDown(event: PointerEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const handleDiscard = (id: string): void => {
    discardFailedOutbox(id)
    refresh() // Stand sofort neu lesen — verlässt den 'failed'-Zustand, sobald der Bucket leer ist
  }

  const count = state === 'failed' ? failed : pending
  const text = label(state, count)
  return (
    // role="status" + aria-label: Screenreader bekommen den vollen Klartext direkt am Indikator (Live-Region).
    <span ref={ref} className={`wf-conn wf-conn--${state}${open ? ' wf-conn--open' : ''}`} role="status" aria-label={text}>
      <span className="wf-conn__pill" aria-hidden="true">
        {pillText(state, count)}
      </span>
      <button
        type="button"
        className="wf-conn__btn"
        aria-label="Verbindungsstatus anzeigen"
        aria-expanded={open}
        title={text}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="wf-conn__dot" aria-hidden="true" />
      </button>
      {open && state === 'failed' && <DeadLetterPanel items={loadFailedOutbox()} onDiscard={handleDiscard} />}
    </span>
  )
}
